import {
  AttributeDefinitionValue,
  AttributeForm,
  CertAndLicensesForm,
  CompanyRef,
  Location,
  Resource,
  ResourceForm,
  SelectedAttributeBoolForm,
  SelectedAttributeDefinitions,
  SelectedAttributeValuesBools,
  SelectedAttributeValuesVarchars,
  SelectedCertificationsAndLicenses,
  SubAttribute,
} from '@dispatch/shared/types';
import { isNil } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { formatLocationDate } from './DayHelper';

/**
 * A blank boolean-attribute form value. `value` is left `undefined` so an
 * untouched checkbox normalizes to an empty array at submit time (rather than
 * persisting an unintended `false`). Used to seed newly-added attributes.
 */
export const emptyBoolForm = (): SelectedAttributeBoolForm => ({
  integrationGUID: '',
  selectedAttributeDefinitionIntegrationGUID: '',
  attributeDefinitionValueIntegrationGUID: '',
  title: '',
  value: undefined,
});

/**
 * @param fieldCapacityByDayEnabled per-tenant kill-switch for the per-day field
 * capacity model (ADO #94651). Defaults to `false` so an unresolved flag emits
 * today's payload.
 *
 * The submitted shape always matches what was rendered, and is decided by TWO switches: the
 * tenant flag decides whether per-day capacity is available at all, and the per-resource
 * `fieldCapacityMode` decides which form this resource uses.
 *
 * - flag off -> submit `claimCapacity`, omit both `fieldCapacityByDay` and `fieldCapacityMode`
 *   (a tenant without the feature must not be able to mark resources PER_DAY).
 * - flag on + STATIC -> submit `claimCapacity` and the mode; omit `fieldCapacityByDay`.
 * - flag on + PER_DAY -> submit `fieldCapacityByDay`, `claimCapacity` AND the mode.
 *
 * The scalar goes up in per-day mode too because the two co-exist on a resource: the server
 * stores both and picks between them at resolve time, so `claimCapacity` is the resource's
 * fallback capacity (live if per-day is switched off for the tenant, or if the resource is
 * moved back to Static) rather than a value derived from the grid. The General tab renders it
 * in both modes for that reason, and omitting it here would make that input un-saveable.
 *
 * Submitting the mode is not optional. The server resolves per-day capacity ONLY for resources
 * explicitly marked PER_DAY: it checks the mode before the date-override and day-of-week
 * lookups, so per-day values written without a mode are persisted but never read. Omitting it
 * would leave a dispatcher configuring a grid that silently has no effect.
 */
export const ResourceFormHelper = (
  values: ResourceForm,
  data: Resource,
  tenantId: string,
  fieldCapacityByDayEnabled = false
): Resource => {
  const { calculatedSumOfCapacities, ...rest } = values.claimOwner;
  const claimOwnerCapacity = { ...rest };

  return {
    cwsUserId: values?.generalInformation?.cwsUserId || undefined,
    isStandby: data?.isStandby,
    resourceName: values?.generalInformation?.name,
    tenantId: tenantId,
    createdAt: data?.createdAt,
    updatedAt: new Date().toISOString(),
    integrationGUID: data?.integrationGUID || uuidv4(),
    isActive: data?.isActive || false, // the only time this will be null is creating a new resource
    name: values?.generalInformation?.name,
    description: values?.generalInformation?.description,
    email: values?.generalInformation?.email,
    phone: values?.generalInformation?.phone || '',
    commuteTimeInMin: values?.generalInformation?.commuteTimeInMin,
    isContingent: values?.teamsOfficesRoles?.isContingent,
    isWorkTeam: values?.generalInformation?.isWorkTeam,
    isAlwaysAvailable: values?.generalInformation?.isAlwaysAvailable,
    lockImmediately:
      values?.generalInformation?.isWorkTeam === true
        ? values?.generalInformation?.lockImmediately
        : false,
    contingentCompany: values?.teamsOfficesRoles?.isContingent
      ? values?.teamsOfficesRoles?.contingentCompany
      : null,
    wipLimit: values?.generalInformation?.wipLimit,
    claimOwnerCapacity,
    // Zero is a valid capacity (AC5), so `|| 0` only fills in a missing value.
    ...(fieldCapacityByDayEnabled
      ? {
          fieldCapacityMode: values.fieldCapacityMode ?? 'STATIC',
          claimCapacity: values?.generalInformation?.claimCapacity || 0,
          ...(values.fieldCapacityMode === 'PER_DAY'
            ? { fieldCapacityByDay: values.fieldCapacityByDay }
            : {}),
        }
      : { claimCapacity: values?.generalInformation?.claimCapacity || 0 }),
    virtualClaimCapacity: values?.generalInformation?.virtualClaimCapacity || 0,
    complexLossClaimCapacity:
      values?.generalInformation?.complexLossClaimCapacity || 0,
    efficiency: values?.generalInformation?.efficiency,
    companies: getCompaniesData(values, data),
    offices:
      values?.teamsOfficesRoles?.offices?.map((integrationGuid) => {
        return { integrationGUID: integrationGuid };
      }) || [],
    teams:
      values?.teamsOfficesRoles?.teams?.map((integrationGuid) => {
        return { integrationGUID: integrationGuid };
      }) || [],
    groups:
      values?.teamsOfficesRoles?.groups?.map((integrationGuid) => {
        return { integrationGUID: integrationGuid };
      }) || [],
    roles:
      values?.teamsOfficesRoles?.roles?.map((integrationGuid) => {
        return { integrationGUID: integrationGuid };
      }) || [],
    locations:
      values?.locations?.map((location): Location => {
        return {
          integrationGUID: location.integrationGUID || uuidv4(),
          name: location.locationInformation?.name || '',
          street: location.locationInformation?.street || '',
          unit: location.locationInformation?.unit || '',
          city: location.locationInformation?.city || '',
          state: location.locationInformation?.state || '',
          zip: location.locationInformation?.zip || '',
          lat: location.locationInformation?.lat,
          long: location.locationInformation?.long,
          isStartingLocation: location.serviceInformation?.isStartingLocation,
          isVirtualLocation: JSON.parse(
            location.locationInformation?.isVirtualLocation ?? 'false'
          ),
          startDate: formatLocationDate(location.serviceInformation?.startDate),
          endDate: formatLocationDate(location.serviceInformation?.endDate),
          allowedTravelRadius: location.serviceInformation?.allowedTravelRadius,
          monday: location.serviceInformation?.monday,
          tuesday: location.serviceInformation?.tuesday,
          wednesday: location.serviceInformation?.wednesday,
          thursday: location.serviceInformation?.thursday,
          friday: location.serviceInformation?.friday,
          saturday: location.serviceInformation?.saturday,
          sunday: location.serviceInformation?.sunday,
          exclusionZones: {
            states: location.exclusionZonesInformation?.states || [],
            counties:
              location.exclusionZonesInformation?.counties?.map((county) => {
                if (typeof county === 'string') {
                  return county;
                }

                return county.value as string;
              }) || [],
            zipCodes: location.exclusionZonesInformation?.zipCodes || [],
          },
          inclusionZones: {
            states: location.inclusionZonesInformation?.states || [],
            counties:
              location.inclusionZonesInformation?.counties?.map((county) => {
                if (typeof county === 'string') {
                  return county;
                }

                return county.value as string;
              }) || [],
            zipCodes: location.inclusionZonesInformation?.zipCodes || [],
          },
        };
      }) || [],
    selectedAttributeDefinitions: createSelectedAttributeDefinitions(
      values.attributes,
      data
    ),
    selectedAttributeGroups: values.selectedAttributeGroups,
    attributeFilterSelection: values.attributeFilterSelection,
    selectedCertificationsAndLicenses: createSelectedCertsAndLicenses(
      values.certsAndLicenses,
      data
    ),
  };
};

const createSelectedAttributeDefinitions = (
  attributes: AttributeForm[],
  data: Resource
): SelectedAttributeDefinitions[] => {
  return (
    attributes?.map((attribute): SelectedAttributeDefinitions => {
      const companyAttribute = data?.company?.attributeDefinition?.find(
        (companyAttr) =>
          companyAttr.integrationGUID ===
          attribute.attributeDefinitionIntegrationGUID
      );

      const isSelectedVarChar =
        companyAttribute?.attributeDataType?.includes('string');

      return {
        integrationGUID: attribute.integrationGUID || uuidv4(),
        attributeDefinitionIntegrationGUID:
          companyAttribute?.integrationGUID || '',
        displayName: attribute?.displayName || '',
        selectedAttributeValuesVarchars: isSelectedVarChar
          ? getSelectedAttributeValuesVarChar(
              companyAttribute?.attributeDefinitionValues || [],
              attribute
            )
          : [],
        selectedAttributeValuesInts: [],
        selectedAttributeValuesFloats: [],
        selectedAttributeValuesBools: !isSelectedVarChar
          ? getSelectAttributeValuesBool(
              companyAttribute?.attributeDefinitionValues || [],
              attribute
            )
          : [],
      };
    }) || []
  );
};

const getSelectedAttributeValuesVarChar = (
  attributeDefinitionValues: AttributeDefinitionValue[],
  attribute: AttributeForm
): SelectedAttributeValuesVarchars[] => {
  return attribute?.selectedAttributeValuesVarchars?.map(
    (attrValue): SelectedAttributeValuesVarchars => {
      const companyAttributeValue = attributeDefinitionValues.find(
        (companyAttributeValue) => {
          if (typeof attrValue === 'string') {
            return companyAttributeValue.integrationGUID === attrValue;
          }

          return (
            companyAttributeValue.integrationGUID ===
            attrValue.attributeDefinitionValueIntegrationGUID
          );
        }
      );

      return {
        selectedAttributeDefinitionIntegrationGUID: attribute?.integrationGUID,
        displayName: companyAttributeValue?.displayName || '',
        integrationGUID: attrValue?.integrationGUID || uuidv4(),
        attributeDefinitionValueIntegrationGUID:
          companyAttributeValue?.integrationGUID || '',
        value: companyAttributeValue?.value || '',
        startDate: attrValue.startDate,
        endDate: attrValue.endDate,
        subAttributes: attrValue?.subAttributes?.map((subAttribute) => {
          subAttribute.value = subAttribute?.displayName;
          return getSubAttributeValue(subAttribute);
        }),
      };
    }
  );
};

const getSubAttributeValue = (subAttribute: SubAttribute) => ({
  ...subAttribute,
  startDate: formatLocationDate(subAttribute.startDate),
  endDate: formatLocationDate(subAttribute.endDate),
});

const getSelectAttributeValuesBool = (
  attributeDefinitionValues: AttributeDefinitionValue[],
  attribute: AttributeForm
): SelectedAttributeValuesBools[] => {
  const bool = attribute?.selectedAttributeValuesBools;

  // No boolean chosen (null/undefined): emit an empty array rather than fanning
  // an absent value across every definition value. An explicit `false` is a
  // real value and is kept.
  if (isNil(bool?.value)) {
    return [];
  }

  return attributeDefinitionValues.map(
    (attrValue): SelectedAttributeValuesBools => {
      return {
        selectedAttributeDefinitionIntegrationGUID: attribute?.integrationGUID,
        displayName: attrValue.displayName,
        integrationGUID: bool?.integrationGUID || uuidv4(),
        attributeDefinitionValueIntegrationGUID: attrValue.integrationGUID,
        value: bool?.value,
        startDate: bool?.startDate,
        endDate: bool?.endDate,
      };
    }
  );
};

const createSelectedCertsAndLicenses = (
  certsAndLicenses: CertAndLicensesForm[],
  data: Resource
): SelectedCertificationsAndLicenses[] => {
  return (
    certsAndLicenses?.map((selectedCert): SelectedCertificationsAndLicenses => {
      const companyCert = data?.company?.certificationAndLicense.find(
        (companyCerts) =>
          companyCerts.integrationGUID ===
          selectedCert.certificationAndLicenseIntegrationGUID
      );

      return {
        integrationGUID: selectedCert.integrationGUID || uuidv4(),
        certificationAndLicenseIntegrationGUID:
          companyCert?.integrationGUID || '',
        name: companyCert?.name || '',
        licenseNumber: selectedCert.licenseNumber,
        verificationStatus: selectedCert.verificationStatus,
        documentCount: selectedCert.documentCount,
        issueDate: selectedCert.issueDate,
        expirationDate: selectedCert.expirationDate,
        verificationType: companyCert?.verificationType || '',
        organization: companyCert?.organization || '',
      };
    }) || []
  );
};

const getCompaniesData = (
  values: ResourceForm,
  data: Resource
): CompanyRef[] => {
  return (
    data?.companies?.map((company) => {
      if (company.integrationGUID === data?.company?.integrationGUID) {
        return {
          ...company,
          externalResourceId: values?.generalInformation?.externalResourceId,
        };
      }

      return company;
    }) || []
  );
};

export const updateCertsAndLicenses = (
  selectedCertificationsAndLicenses: SelectedCertificationsAndLicenses[]
) => {
  return selectedCertificationsAndLicenses.map((certAndLicense) => {
    return {
      integrationGUID: certAndLicense.integrationGUID,
      certificationAndLicenseIntegrationGUID:
        certAndLicense.certificationAndLicenseIntegrationGUID,
      licenseNumber: certAndLicense.licenseNumber,
      verificationStatus: certAndLicense.verificationStatus,
      documentCount: certAndLicense.documentCount,
      issueDate: certAndLicense.issueDate,
      expirationDate: certAndLicense.expirationDate || '',
    };
  });
};
